import { INTEGRATIONS_MAP } from "features/integrations/constants";
import useIntegrationSettings from "features/integrations/datahooks/useIntegrationSettings";
import {
  ContentContainer,
  ContentHeader,
  PageTitle,
} from "features/layout/components";

import { typedObjectEntries } from "common/helpers/utils";

import CrmIntegrationSettings from "./CrmIntegrationSettings";
import EmailOutreachConnect from "./EmailOutreachConnect";
import EmailOutreachSettings from "./EmailOutreachSettings";
import IntegrationItem from "./IntegrationItem";
import Rb2bIntegrationSettings from "./Rb2bIntegrationSettings";

import type { ComponentProps, JSX } from "react";

export default function IntegrationsList() {
  const { integrationSettings } = useIntegrationSettings();

  return (
    <ContentContainer>
      <ContentHeader>
        <PageTitle />
      </ContentHeader>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
        {typedObjectEntries(INTEGRATIONS_MAP).map(
          ([key, { icon, title, description, ...rest }]) => {
            const commonIntegrationItemProps = {
              icon,
              type: key,
              description,
            };
            let integrationItemProps:
              | { redirectUrl: string }
              | { settingsComponent: JSX.Element; hasError?: boolean };

            if ("redirectUrl" in rest) {
              integrationItemProps = { redirectUrl: rest.redirectUrl };
            } else {
              if (!integrationSettings) {
                return (
                  <IntegrationItem
                    key={key}
                    {...commonIntegrationItemProps}
                    isLoading
                  />
                );
              }

              if (
                key === "instantly" ||
                key === "smartlead" ||
                key === "emailbison"
              ) {
                let settingsComponent = <EmailOutreachConnect type={key} />;
                let errorMessage: string;

                if (integrationSettings[key]) {
                  const settings = integrationSettings[key];
                  let settingsProps: ComponentProps<
                    typeof EmailOutreachSettings
                  >;

                  if ("error" in settings) {
                    errorMessage = settings.error;
                    settingsProps =
                      key === "emailbison"
                        ? { type: "emailbison", errorMessage }
                        : { type: "instantly", errorMessage };
                  } else if ("workspace_name" in settings) {
                    settingsProps = {
                      type: "instantly",
                      workspaceName: settings.workspace_name,
                    };
                  } else if ("user_name" in settings) {
                    settingsProps = {
                      type: "emailbison",
                      userName: settings.user_name,
                    };
                  } else {
                    settingsProps = {
                      type: "smartlead",
                      apiKey: settings.api_key,
                    };
                  }

                  settingsComponent = (
                    <EmailOutreachSettings key={key} {...settingsProps} />
                  );
                }

                integrationItemProps = {
                  settingsComponent,
                  hasError: !!errorMessage,
                };
              } else if (key === "hubspot" || key === "pipedrive") {
                const settings = integrationSettings[key];
                integrationItemProps =
                  "install_url" in settings
                    ? { redirectUrl: settings.install_url }
                    : {
                        settingsComponent: (
                          <CrmIntegrationSettings
                            {...("error" in settings
                              ? { errorMessage: settings.error }
                              : { settings: settings })}
                            crmExportType={key}
                          />
                        ),
                      };
              } else if (key === "rb2b") {
                integrationItemProps = {
                  settingsComponent: (
                    <Rb2bIntegrationSettings
                      settings={integrationSettings[key]}
                    />
                  ),
                };
              }
            }

            return (
              <IntegrationItem
                key={key}
                {...commonIntegrationItemProps}
                {...integrationItemProps}
              />
            );
          },
        )}
      </div>
    </ContentContainer>
  );
}
